/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import {
  MONTH_OPTIONS,
  fetchMultiplePayslipPdf,
  downloadBase64Pdf,
  base64ToObjectUrl,
  getLoggedInEmpId,
} from "../salaryStatement/salaryStatement.service";

const PRIMARY = "#C0282C";
const YEAR_OPTIONS = [2020, 2021, 2022, 2023, 2024, 2025, 2026];

const MultipleSalaryStatement: React.FC = () => {
  const empId = useMemo(() => getLoggedInEmpId(), []);

  // 0-indexed months — same defaults as the mobile app
  const [fromMonth, setFromMonth] = useState(2); // March
  const [fromYear, setFromYear] = useState(2024);
  const [toMonth, setToMonth] = useState(2); // March
  const [toYear, setToYear] = useState(2026);

  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState("");

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const fromLabel = `${MONTH_OPTIONS[fromMonth]} ${fromYear}`;
  const toLabel = `${MONTH_OPTIONS[toMonth]} ${toYear}`;

  const handleDownload = useCallback(async () => {
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }

    const fromTimestamp = new Date(fromYear, fromMonth, 1).getTime();
    const toTimestamp = new Date(toYear, toMonth, 1).getTime();
    if (fromTimestamp > toTimestamp) {
      toast.error("From date cannot be after to date");
      return;
    }

    setLoading(true);
    try {
      const result = await fetchMultiplePayslipPdf({
        fromMonth: fromMonth + 1,
        fromYear,
        toMonth: toMonth + 1,
        toYear,
        empId,
      });

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(base64ToObjectUrl(result.base64));
      setPreviewName(result.fileName);
      downloadBase64Pdf(result.base64, result.fileName);
      toast.success("Downloaded successfully");
    } catch (err: any) {
      toast.error(err?.message || "Download failed");
    } finally {
      setLoading(false);
    }
  }, [empId, fromMonth, fromYear, toMonth, toYear, previewUrl]);

  const hidePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewName("");
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Multiple Salary Statement</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Other Services</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Multiple Salary Statement
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleDownload}
                disabled={loading}
                title="Download Multiple Salary Statement PDF"
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                ) : (
                  <i className="ti ti-download me-2" />
                )}
                {loading ? "Downloading..." : "Download PDF"}
              </button>
            </li>
          </ul>
        </div>

        <div className="mss-page">
          <div className="mss-card">
            <h6 className="mss-card-title">Instructions</h6>
            <ul className="mss-instructions">
              <li>
                - Select From and To period for fetch multiple salary statement.
              </li>
              <li>- Bonus and Offcycle payment are excluded.</li>
              <li>- Only block-period payslips can be downloaded.</li>
            </ul>
          </div>

          <div className="mss-card">
            <h6 className="mss-card-title">From Period</h6>
            <div className="mss-picker-row">
              <PeriodSelect
                value={String(fromMonth)}
                onChange={(v) => setFromMonth(Number(v))}
                disabled={loading}
                options={MONTH_OPTIONS.map((label, index) => ({
                  value: String(index),
                  label,
                }))}
              />
              <PeriodSelect
                value={String(fromYear)}
                onChange={(v) => setFromYear(Number(v))}
                disabled={loading}
                options={YEAR_OPTIONS.map((year) => ({
                  value: String(year),
                  label: String(year),
                }))}
              />
            </div>
          </div>

          <div className="mss-card">
            <h6 className="mss-card-title">To Period</h6>
            <div className="mss-picker-row">
              <PeriodSelect
                value={String(toMonth)}
                onChange={(v) => setToMonth(Number(v))}
                disabled={loading}
                options={MONTH_OPTIONS.map((label, index) => ({
                  value: String(index),
                  label,
                }))}
              />
              <PeriodSelect
                value={String(toYear)}
                onChange={(v) => setToYear(Number(v))}
                disabled={loading}
                options={YEAR_OPTIONS.map((year) => ({
                  value: String(year),
                  label: String(year),
                }))}
              />
            </div>
          </div>

          <div className="mss-summary">
            Selected Period: {fromLabel} - {toLabel}
          </div>

          <div className="mss-download-wrap">
            <button
              type="button"
              className="mss-download-btn"
              onClick={handleDownload}
              disabled={loading}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                <i className="ti ti-download" />
              )}
              <span>{loading ? "Downloading..." : "Download"}</span>
            </button>
          </div>
        </div>

        {previewUrl && (
          <div className="card mt-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Multiple Salary Statement PDF</h5>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleDownload}
                  disabled={loading}
                  title="Download PDF File"
                >
                  {loading ? (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    />
                  ) : (
                    <i className="ti ti-download me-1" />
                  )}
                  Download
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={hidePreview}
                  title="Hide PDF"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <div className="card-body p-0">
              <div style={{ width: "100%", height: "800px" }}>
                <iframe
                  src={`${previewUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title={previewName || "Multiple Salary Statement PDF"}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .mss-page {
          max-width: 720px;
        }
        .mss-card {
          background: #fff;
          border-radius: 12px;
          padding: 16px 18px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        .mss-card-title {
          color: ${PRIMARY};
          font-weight: 600;
          font-size: 15px;
          margin-bottom: 10px;
        }
        .mss-instructions {
          margin: 0;
          padding-left: 18px;
          color: #6b7280;
          font-size: 14px;
        }
        .mss-instructions li {
          margin-bottom: 4px;
        }
        .mss-picker-row {
          display: flex;
          gap: 12px;
        }
        .mss-select-wrap {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #fff;
          padding: 0 10px;
        }
        .mss-select-wrap i.ti-calendar {
          color: ${PRIMARY};
          font-size: 16px;
          pointer-events: none;
        }
        .mss-select-wrap select {
          flex: 1;
          border: none;
          background: transparent;
          padding: 10px 8px;
          appearance: none;
          -webkit-appearance: none;
          outline: none;
          font-size: 14px;
          color: #111827;
          cursor: pointer;
        }
        .mss-select-wrap i.ti-chevron-down {
          color: #9ca3af;
          font-size: 16px;
          pointer-events: none;
        }
        .mss-summary {
          background: ${PRIMARY}14;
          border: 1px solid ${PRIMARY}33;
          border-radius: 10px;
          padding: 14px 16px;
          text-align: center;
          color: ${PRIMARY};
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 20px;
        }
        .mss-download-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 8px;
        }
        .mss-download-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-width: 160px;
          background: ${PRIMARY};
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 10px 28px;
          font-weight: 600;
          font-size: 15px;
          box-shadow: 0 4px 10px rgba(192, 40, 44, 0.25);
        }
        .mss-download-btn:disabled {
          opacity: 0.65;
        }
        .mss-download-btn:not(:disabled):hover {
          filter: brightness(0.95);
        }
        @media (max-width: 576px) {
          .mss-picker-row {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

const PeriodSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  options: { value: string; label: string }[];
}> = ({ value, onChange, disabled, options }) => (
  <div className="mss-select-wrap">
    <i className="ti ti-calendar" />
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    <i className="ti ti-chevron-down" />
  </div>
);

export default MultipleSalaryStatement;
